/**
 * Pulse — survey builder.
 *
 * HR_ADMIN-only. Lets the admin compose a survey title + frequency
 * + anonymity toggle and stack questions. Saves the whole thing in
 * a single POST to /pulse/surveys (the backend creates the survey
 * + question rows atomically).
 *
 * Edit mode reuses this same page mounted at /pulse/:id/edit — we
 * pre-load the survey on mount and PATCH the fields back. New
 * questions added in edit mode go through /pulse/{id}/questions.
 */
import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Plus, Trash2 } from "lucide-react";

import {
  addQuestions,
  createSurvey,
  getSurvey,
  updateSurvey,
  type PulseFrequency,
  type PulseQuestionInput,
  type PulseQuestionType,
} from "@/api/pulse";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { Input } from "@/components/ui/Input";
import { Select } from "@/components/ui/Select";
import { PageHeader } from "@/components/layout/PageHeader";
import { useToast } from "@/hooks/useToast";
import { pageTransition } from "@/lib/utils";

interface DraftQuestion {
  question_text: string;
  question_type: PulseQuestionType;
  options: string;  // newline-separated; parsed on save
  // Mark questions that were already saved on the backend (edit mode)
  // so we know to skip them on the second-pass /questions POST.
  existing_id?: string;
}

const FREQ_OPTIONS: { value: PulseFrequency; label: string }[] = [
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "quarterly", label: "Quarterly" },
  { value: "one_time", label: "One-time" },
];

const TYPE_OPTIONS: { value: PulseQuestionType; label: string }[] = [
  { value: "scale_1_5", label: "Scale 1–5" },
  { value: "scale_1_10", label: "Scale 0–10 (eNPS-style)" },
  { value: "text", label: "Free text" },
  { value: "multiple_choice", label: "Multiple choice" },
];

export function PulseBuilderPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { addToast } = useToast();

  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [frequency, setFrequency] = useState<PulseFrequency>("monthly");
  const [isAnonymous, setIsAnonymous] = useState(true);
  const [questions, setQuestions] = useState<DraftQuestion[]>([
    { question_text: "", question_type: "scale_1_5", options: "" },
  ]);
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isEdit || !id) return;
    let cancelled = false;
    getSurvey(id)
      .then((s) => {
        if (cancelled) return;
        setTitle(s.title);
        setDescription(s.description || "");
        setFrequency(s.frequency);
        setIsAnonymous(s.is_anonymous);
        setQuestions(
          s.questions.map((q) => ({
            existing_id: q.id,
            question_text: q.question_text,
            question_type: q.question_type,
            options: (q.options || []).join("\n"),
          })) || [],
        );
      })
      .catch(() => addToast("error", "Could not load survey."))
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [id, isEdit, addToast]);

  const updateQ = (idx: number, patch: Partial<DraftQuestion>) => {
    setQuestions((prev) => prev.map((q, i) => (i === idx ? { ...q, ...patch } : q)));
  };
  const addQ = () =>
    setQuestions((prev) => [
      ...prev,
      { question_text: "", question_type: "scale_1_5", options: "" },
    ]);
  const removeQ = (idx: number) =>
    setQuestions((prev) => prev.filter((_, i) => i !== idx));

  const buildPayloadQuestions = (): PulseQuestionInput[] => {
    return questions.map((q, idx) => ({
      question_text: q.question_text.trim(),
      question_type: q.question_type,
      // Multi-choice: split newline-separated options; otherwise null.
      options:
        q.question_type === "multiple_choice"
          ? q.options
              .split("\n")
              .map((o) => o.trim())
              .filter(Boolean)
          : null,
      order_index: idx,
    }));
  };

  const validate = (qs: PulseQuestionInput[]): string | null => {
    if (!title.trim()) return "Survey needs a title.";
    if (qs.length === 0) return "Add at least one question.";
    for (const q of qs) {
      if (!q.question_text) return "Every question needs text.";
      if (q.question_type === "multiple_choice") {
        if (!q.options || q.options.length < 2) {
          return `"${q.question_text.slice(0, 30)}…" needs ≥2 options.`;
        }
      }
    }
    return null;
  };

  const handleSave = async () => {
    const payloadQs = buildPayloadQuestions();
    const err = validate(payloadQs);
    if (err) {
      addToast("error", err);
      return;
    }
    setSubmitting(true);
    try {
      if (isEdit && id) {
        await updateSurvey(id, {
          title: title.trim(),
          description: description.trim() || undefined,
          frequency,
          is_anonymous: isAnonymous,
        });
        // Append any NEW questions in edit mode. Editing existing
        // questions in-place is a future enhancement — today we just
        // surface them as read-only on the row.
        const newOnes = questions
          .map((q, idx) => ({ q, idx }))
          .filter(({ q }) => !q.existing_id);
        if (newOnes.length > 0) {
          await addQuestions(
            id,
            newOnes.map(({ q, idx }) => ({
              question_text: q.question_text.trim(),
              question_type: q.question_type,
              options:
                q.question_type === "multiple_choice"
                  ? q.options.split("\n").map((o) => o.trim()).filter(Boolean)
                  : null,
              order_index: idx,
            })),
          );
        }
        addToast("success", "Survey updated.");
      } else {
        await createSurvey({
          title: title.trim(),
          description: description.trim() || undefined,
          frequency,
          is_anonymous: isAnonymous,
          questions: payloadQs,
        });
        addToast("success", "Survey created.");
      }
      navigate("/pulse");
    } catch (e: unknown) {
      const detail =
        (e as { response?: { data?: { detail?: string } } })?.response?.data
          ?.detail || "Could not save survey.";
      addToast("error", detail);
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <div className="space-y-3">
        <div className="h-10 rounded bg-border/30 animate-pulse" />
        <div className="h-64 rounded bg-border/30 animate-pulse" />
      </div>
    );
  }

  return (
    <motion.div {...pageTransition}>
      <PageHeader
        title={isEdit ? "Edit survey" : "New pulse survey"}
        subtitle="Add a title, schedule, and questions. Send when you're ready."
      />

      <Card className="space-y-4">
        <Input
          label="Survey title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          placeholder="e.g. Q2 Engagement Check-in"
        />
        <div>
          <label className="block text-sm font-medium text-text-primary mb-1">
            Description (optional)
          </label>
          <textarea
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
            className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm placeholder:text-text-muted focus:outline-none focus:ring-2 focus:ring-accent/50"
            placeholder="What's this survey about?"
          />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Select
            label="Frequency"
            value={frequency}
            onChange={(e) => setFrequency(e.target.value as PulseFrequency)}
            options={FREQ_OPTIONS}
          />
          <label className="flex items-center gap-3 mt-6 sm:mt-0 sm:self-end">
            <input
              type="checkbox"
              checked={isAnonymous}
              onChange={(e) => setIsAnonymous(e.target.checked)}
              className="h-4 w-4 rounded border-border text-accent focus:ring-accent"
            />
            <span className="text-sm text-text-primary">
              Anonymous responses
            </span>
          </label>
        </div>
      </Card>

      <div className="space-y-3 mt-4">
        {questions.map((q, idx) => (
          <Card key={idx} className="space-y-3">
            <div className="flex items-start gap-3">
              <div className="flex-1 space-y-2">
                <Input
                  label={`Question ${idx + 1}`}
                  value={q.question_text}
                  onChange={(e) => updateQ(idx, { question_text: e.target.value })}
                  disabled={Boolean(q.existing_id)}
                />
                <Select
                  label="Type"
                  value={q.question_type}
                  onChange={(e) =>
                    updateQ(idx, { question_type: e.target.value as PulseQuestionType })
                  }
                  disabled={Boolean(q.existing_id)}
                  options={TYPE_OPTIONS}
                />
                {q.question_type === "multiple_choice" && (
                  <div>
                    <label className="block text-sm font-medium text-text-primary mb-1">
                      Options (one per line)
                    </label>
                    <textarea
                      value={q.options}
                      onChange={(e) => updateQ(idx, { options: e.target.value })}
                      rows={3}
                      className="w-full rounded-lg border border-border bg-surface px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/50"
                      placeholder={"Strongly agree\nAgree\nNeutral\nDisagree\nStrongly disagree"}
                      disabled={Boolean(q.existing_id)}
                    />
                  </div>
                )}
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={() => removeQ(idx)}
                aria-label={`Remove question ${idx + 1}`}
                disabled={Boolean(q.existing_id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </div>
          </Card>
        ))}
        <Button variant="secondary" onClick={addQ}>
          <Plus className="h-4 w-4" />
          Add question
        </Button>
      </div>

      <div className="mt-6 flex gap-3 justify-end">
        <Button variant="ghost" onClick={() => navigate("/pulse")}>
          Cancel
        </Button>
        <Button onClick={handleSave} isLoading={submitting}>
          {isEdit ? "Save changes" : "Create survey"}
        </Button>
      </div>
    </motion.div>
  );
}
